import cn from "classnames";
import Styles from "./EventHeaderFinal.module.scss";
import Common from "./EventHeaderCommon.module.scss";
import * as Team from "src/state/modules/team";
import * as Event from "src/state/modules/event";
import { Avatar } from "src/components/atoms/Avatar/Avatar";
import { EventHeaderBatsFinal } from "./EventHeaderBatsFinal";
import { Text } from "src/components/atoms/Text/Text";
import { LineScore } from "../LineScores/LineScore";
import { UseGameStateResult } from "src/components/util/useGameState";
import { gamePitchBounds, ordinalInning } from "src/components/pages/Events/game-stream-helpers";
import { teamAvatarUrl } from "src/utils/team-avatar";

interface EventHeaderFinalProps {
  event: Event.Event;
  team: Team.Team;
  gameState?: UseGameStateResult;
}

export default function EventHeaderFinal({ event, team, gameState }: EventHeaderFinalProps) {
  const snap = gameState?.state ?? null;
  const built = gameState?.built ?? null;
  // The summary's game_status can stay "live"/"new" after a game ends, so also require that
  // sabertooth hasn't already decided the game (a finished game has a winning/losing pitcher).
  const isLive = event.gameData?.game_state === Event.GameState.LIVE && !built?.gameOver;
  // Who's home/away: trust the game stream's set_teams (the same source the box score and
  // play-by-play use) so the linescore row order stays consistent with them — away always on the
  // top row. event.homeAway is schedule metadata that can disagree (it had the rows flipped vs. the
  // box score), so it's only the pre-stream fallback.
  const isHome = built ? built.homeTeamId === team?.id : event.homeAway === "home";

  // Resolve each row's GC team id by matching our team.id (same convention as the box score). This
  // keeps every row's label, big score, line score, and arrow pointing at the same team.
  const ourSaberId = built ? (built.homeTeamId === team?.id ? built.homeTeamId : built.awayTeamId) : undefined;
  const oppSaberId = built ? (built.homeTeamId === team?.id ? built.awayTeamId : built.homeTeamId) : undefined;
  const homeSaberId = isHome ? ourSaberId : oppSaberId;
  const awaySaberId = isHome ? oppSaberId : ourSaberId;

  // Line-score totals are authoritative (live & final); fall back to summary totals until built.
  const scoreForId = (id?: string) =>
    id ? snap?.linescoreWithStatTotals?.[id]?.totalRuns ?? snap?.scores?.[id] : undefined;
  const teamScore = event.gameData?.team_score ?? 0;
  const opponentScore = event.gameData?.opponent_score ?? 0;
  const homeScore = scoreForId(homeSaberId) ?? (isHome ? teamScore : opponentScore);
  const awayScore = scoreForId(awaySaberId) ?? (isHome ? opponentScore : teamScore);

  const ourScore = isHome ? homeScore : awayScore;
  const oppScore = isHome ? awayScore : homeScore;
  const getResult = () => {
    if (ourScore > oppScore) return "won";
    if (ourScore < oppScore) return "lost";
    return "tie";
  };

  const selectedTeam = {
    source: teamAvatarUrl(team?.id),
    name: team?.name,
  };
  const opponentTeam = {
    source: teamAvatarUrl(event?.opponentTeam?.id),
    name: event?.opponentTeam?.name ?? "TBD",
  };
  // `lost` (winner highlighting) only applies to finished games.
  const home = { ...(isHome ? selectedTeam : opponentTeam), score: homeScore, lost: !isLive && homeScore < awayScore };
  const away = { ...(isHome ? opponentTeam : selectedTeam), score: awayScore, lost: !isLive && awayScore < homeScore };
  const isTie = homeScore === awayScore;

  // First/last pitch times anchor the header game clock (first pitch, not the scheduled start).
  const { firstMs, lastMs } = snap ? gamePitchBounds(snap) : { firstMs: null, lastMs: null };

  // Live status label, e.g. "Top 4th · 1 Out".
  const liveLabel =
    isLive && snap
      ? `${snap.half === "top" ? "Top" : "Bot"} ${ordinalInning(snap.inning)} · ${snap.outs} Out${
          snap.outs === 1 ? "" : "s"
        }`
      : undefined;

  return (
    <div className={Styles.eventHeaderGameContainer} data-testid="Event-Header-BatsFinal">
      <div className={Styles.mainContentContainer}>
        <EventHeaderBatsFinal
          event={event}
          outcome={getResult()}
          isLive={isLive}
          liveLabel={liveLabel}
          firstPitchMs={firstMs}
          lastPitchMs={lastMs}
        />
        <LineScore
          isTie={isTie}
          home={home}
          away={away}
          linescore={snap?.linescoreWithStatTotals}
          homeTeamId={homeSaberId}
          awayTeamId={awaySaberId}
          isLive={isLive}
          inningsPerGame={team?.inningsPerGame}
          currentInning={snap?.inning}
        />
      </div>
      <Text size="xxlarge" weight="bold" className={cn(Common.teamScore, Styles.mobileScore)}>
        {away.score + " - " + home.score}
      </Text>
      <Text
        size="xxlarge"
        weight="bold"
        color="white"
        className={cn(Common.teamScore, Styles.homeScore, Common.homeTeamScore)}
      >
        {home.score}
      </Text>
      <Text
        size="xxlarge"
        weight="bold"
        color="white"
        className={cn(Common.teamScore, Styles.awayScore, Common.awayTeamScore)}
      >
        {away.score}
      </Text>
      <div
        className={cn(Styles.awayTeamInfo, Common.teamNameAndAvatarContainer, Common.awayTeamNameAndAvatarContainer)}
      >
        <div className={cn(Common["avatar-container"])}>
          <Avatar.Team sportIcon={Team.Sport.BASEBALL} size="medium" source={away.source} />
        </div>
        <Text color="white" weight="xbold" className={Common.teamName}>
          {away.name}
        </Text>
      </div>
      <div
        className={cn(
          Styles.homeTeamInfo,
          Common.teamNameAndAvatarContainer,
          Common.teamNameAndAvatarLink,
          Common.homeTeamNameAndAvatarContainer
        )}
      >
        <Text className={Common["avatar=container"]}>
          <Avatar.Team sportIcon={Team.Sport.BASEBALL} hideSportIcon={true} size="medium" source={home.source} />
        </Text>
        <Text color="white" weight="xbold" className={Common.teamName}>
          {home.name}
        </Text>
      </div>
    </div>
  );
}
